import React from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ArrowRight, Mail } from "lucide-react";
import Reveal from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import {
  LINKS,
  IMAGES,
  CONTACT_HOURS,
  OTHER_HELP,
  ABOUT_TEXT,
} from "../mock";

export default function Contact() {
  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-b from-[#effaf9] to-white pt-16 pb-6 md:pt-20 md:pb-8">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--teal-dark)]">
              Holistic Intuitive Nutrition
            </p>
            <h1 className="font-serif-brand text-5xl md:text-6xl text-purple mt-3">Connect With Us</h1>
            <div className="brand-rule mx-auto mt-5" />
            <p className="text-neutral-600 mt-5 max-w-xl mx-auto">
              Fill out the form below and Coach Kam will get back to you at your preferred time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact form */}
      <section className="max-w-3xl mx-auto px-5 pt-4 pb-14 md:pt-6 md:pb-20">
        <Reveal delay={120}>
          <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 md:p-10">
            <ContactForm />
          </div>
        </Reveal>
      </section>

      {/* Hours */}
      <section className="bg-[var(--teal)] py-14 md:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <Reveal>
            <Clock className="mx-auto mb-4" size={30} />
            <h2 className="font-serif-brand text-3xl md:text-4xl">{CONTACT_HOURS.title}</h2>
            <p className="mt-5 leading-relaxed max-w-2xl mx-auto text-white/90">
              {CONTACT_HOURS.body}
            </p>
            <div className="flex items-center justify-center gap-2 mt-5 text-white/85 text-sm">
              <MapPin size={16} /> {CONTACT_HOURS.location}
            </div>
            <Link
              to="/book"
              className="btn-brand btn-purple mt-8"
            >
              Get Started Today <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Other ways we can help */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <Reveal className="text-center mb-14">
          <h2 className="font-serif-brand text-4xl md:text-5xl text-purple">
            Other ways we can help
          </h2>
          <div className="brand-rule mx-auto mt-4" />
        </Reveal>
        <div className="grid gap-8 md:grid-cols-3">
          {OTHER_HELP.map((o, i) => (
            <Reveal key={o.title} delay={i * 120}>
              <div className="h-full bg-[#f7fbfa] rounded-lg border border-neutral-100 p-8 hover:shadow-lg transition-shadow duration-300 flex flex-col">
                <h3 className="text-purple font-semibold tracking-wide-sm uppercase text-sm mb-4">
                  {o.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-[0.95rem] flex-1">{o.body}</p>
                <Link
                  to={o.title.includes("Astrology") ? "/astrology" : "/book"}
                  className="mt-6 inline-flex items-center gap-2 text-[var(--teal-dark)] font-semibold tracking-wide-sm uppercase text-xs hover:gap-3 transition-all"
                >
                  {o.title.includes("Astrology") ? "Learn more" : "Book a call"} <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Bio + chart */}
      <section className="bg-[#f7fbfa] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-[var(--teal)]/40 rounded-lg" />
              <img
                src={IMAGES.contactKamla}
                alt="Kamla Das"
                className="relative rounded-lg shadow-xl w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-serif-brand text-4xl md:text-5xl text-purple leading-tight">
              Hi, I'm Kamla
            </h2>
            <div className="brand-rule mt-4 mb-6" />
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              {ABOUT_TEXT.slice(0, 2).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/book" className="btn-brand btn-teal">
                Book a Call
              </Link>
              <Link to="/services" className="btn-brand btn-outline-teal">
                Services
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Free chart banner */}
      <section className="max-w-5xl mx-auto px-5 py-16 md:py-20">
        <Reveal>
          <div className="rounded-xl overflow-hidden bg-[#f3edf5] border border-neutral-100 grid md:grid-cols-2">
            <img
              src={IMAGES.contactBanner}
              alt="Birth chart"
              className="w-full h-full object-cover min-h-[240px]"
            />
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <h3 className="font-serif-brand text-2xl md:text-3xl text-purple">
                What your astrology chart can tell you
              </h3>
              <p className="text-neutral-600 leading-relaxed mt-4 text-[0.95rem]">
                A birth chart is a celestial snapshot of the universal energies at play the moment
                you arrive on this planet. It provides a roadmap to understanding how you became YOU.
              </p>
              <a
                href={LINKS.freeChart}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brand btn-purple mt-6 self-start"
              >
                Get your free chart now
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
